import React, { createRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useConnectWallet } from '@web3-onboard/react';
import { WalletState } from '@web3-onboard/core';
import { ethers } from 'ethers';
import abi from 'erc-20-abi';
import { CryptoCurrency } from '@/api/common.types';
import { Button } from '@/components/common';
import { showNotification } from '@/utils/showNotification';
import { useRouter } from 'next/router';
import { Routes } from '@/constants/routes';
import { useUpdateSellTxs } from '@/api/queryHooks/useSellCryptoController';
import { useUpdateMerchantTxn } from '@/api/queryHooks/useMerchantCryptoController';
import { useGetCoinNetworks } from '@/api';
import { useChainsData } from '@/store/chainsData';
import { shallow } from 'zustand/shallow';
import { compareChainId } from '@/utils/functions';
import { useSellCrypto } from '@/store/sellCrypto';
import { useBuyCrypto } from '@/store/buyCrypto';
import { CryptoFrontendStatus } from '@/api/queryHooks/useTransactionsController';

export function normalizeGasLimit(gasLimit: number) {
  const DEFAULT_GAS_BUFFER = 15000;

  return gasLimit + DEFAULT_GAS_BUFFER;
}

export type OnConnectWallet = WalletState | null;

const NOTIFICATION_MESSAGE_PROCESSING =
  "Processing transaction. This may take a few minutes. Please don't close this window until complete.";

interface IConnectWallet {
  onConnect?: (w?: OnConnectWallet) => void;
  isMerchant?: boolean;
  paymentOptions?: {
    depositAddress?: string;
    cryptoAmount?: number;
    cryptoCurrency?: CryptoCurrency;
    selectedNetwork?: string;
    transactionId?: string;
  };
  selectedNetworkContractAddress?: string | null;
  isPlugInPayment?: boolean;
  selectedNetworkContractAddressData?: string | null;
  selectedNetwork?: CryptoCurrency;
  isBuyFlow?: boolean;
}

export interface IConnectWalletRef {
  connect: (selectedNetwork?: CryptoCurrency) => Promise<WalletState[]>;
  disconnect: () => Promise<WalletState[] | null>;
  wallet: WalletState | null;
}

const ConnectWalletBtn = React.forwardRef<IConnectWalletRef, IConnectWallet>(
  ({ onConnect, paymentOptions, isMerchant, selectedNetworkContractAddress, selectedNetwork }, ref) => {
    const [isButtonLoading, setIsButtonLoading] = useState(false);
    const WCSelectedChainErrorShownRef = useRef<boolean | undefined>(undefined);
    const router = useRouter();
    const [{ wallet, connecting }, connectLocal, disconnectLocal] = useConnectWallet();
    const [ethersProvider, setProvider] = useState<ethers.providers.Web3Provider | null>(null);
    const { mutate: updateSellTxs } = useUpdateSellTxs();
    const { mutate: updateMerchantTxs } = useUpdateMerchantTxn();
    const sellState = useSellCrypto((state) => state, shallow);

    const { chainsData } = useChainsData((state) => state, shallow);
    const selectedChain = useMemo(
      () => chainsData?.find((chain) => chain?.token === selectedNetwork),
      [chainsData, selectedNetwork],
    );

    useImperativeHandle(
      ref,
      () => ({
        connect: async () => {
          const wallets = await connectLocal();

          const NEXTContainer = document.querySelector('#__next') as HTMLElement;
          if (NEXTContainer) {
            NEXTContainer.style.filter = '';
          }
          WCSelectedChainErrorShownRef.current = false; // Reset the ref value
          return wallets;
        },
        disconnect: async () => {
          if (wallet) {
            return disconnectLocal(wallet);
          }
          return null;
        },
        wallet,
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [wallet],
    );

    useEffect(() => {
      router.events.on('routeChangeStart', (url) => {
        if ((url === Routes.SIGN_IN || url === Routes.SIGN_UP) && wallet) {
          disconnectLocal(wallet);
        }
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wallet]);

    useEffect(() => {
      // const chainId = wallet?.chains?.[0].id || undefined;

      if (wallet?.provider) {
        setProvider(new ethers.providers.Web3Provider(wallet.provider, 'any'));
      }

      if (wallet) {
        onConnect?.(wallet);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wallet]);

    // Wrong network connected error functionality --- start

    useEffect(() => {
      if (selectedChain && ethersProvider && ethersProvider?.network?.chainId) {
        const isIdsEqual = compareChainId(selectedChain?.id, String(ethersProvider?.network?.chainId));

        if (!isIdsEqual && !WCSelectedChainErrorShownRef.current) {
          showNotification('Please connect to the correct network', 'error', 'top-center');
          WCSelectedChainErrorShownRef.current = true; // Use ref to prevent further notifications
          // Set the state to true after showing the notification
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [WCSelectedChainErrorShownRef.current]);

    // Wrong network connected error functionality --- end

    if (paymentOptions && wallet) {
      return (
        <Button
          type="button"
          size="small"
          iconPosition="right"
          icon="externalLink"
          loading={isButtonLoading}
          onPress={async () => {
            setIsButtonLoading(true);
            if (
              !paymentOptions?.cryptoCurrency ||
              !paymentOptions?.depositAddress ||
              !paymentOptions.cryptoAmount ||
              !paymentOptions.selectedNetwork
            ) {
              setIsButtonLoading(false);
              return;
            }
            try {
              const signer = await ethersProvider?.getSigner();
              if (
                paymentOptions.cryptoCurrency === CryptoCurrency.ETH &&
                paymentOptions.selectedNetwork === CryptoCurrency.ETH
              ) {
                const value = ethers.utils.parseEther(paymentOptions.cryptoAmount.toString());
                const tx = await signer?.sendTransaction({ to: paymentOptions.depositAddress, value });

                if (!tx?.hash || !paymentOptions.transactionId) {
                  showNotification();
                  setIsButtonLoading(false);
                  return null;
                }

                console.log('TX was send');

                // Change the flow status to pending deposit after the transaction is sent from the wallet
                sellState.setData({
                  txData: {
                    ...sellState?.txData,
                    frontendStatus: CryptoFrontendStatus.PENDING_DEPOSIT,
                  },
                });

                if (isMerchant) {
                  updateMerchantTxs(
                    {
                      transactionId: paymentOptions.transactionId,
                      sourceAddress: wallet?.accounts[0].address,
                      blockchainHash: tx.hash,
                    },
                    {
                      onSuccess: () => {
                        setIsButtonLoading(false);
                        showNotification(NOTIFICATION_MESSAGE_PROCESSING, 'success');
                      },
                    },
                  );
                } else {
                  updateSellTxs(
                    {
                      transactionId: paymentOptions.transactionId,
                      sourceAddress: wallet?.accounts[0].address,
                      blockchainHash: tx.hash,
                    },
                    {
                      onSuccess: () => {
                        setIsButtonLoading(false);
                        showNotification(NOTIFICATION_MESSAGE_PROCESSING, 'success');
                      },
                    },
                  );
                }
              } else {
                if (!selectedNetworkContractAddress || selectedNetworkContractAddress?.length === 0) {
                  showNotification('Something went wrong. Please contact support');
                  // eslint-disable-next-line no-console
                  console.error('Invalid network contract address');
                  setIsButtonLoading(false);
                  return;
                }

                const erc20 = new ethers.Contract(selectedNetworkContractAddress, abi, signer);

                const senderBalance = await erc20.balanceOf(signer?.getAddress());

                const decimals = await erc20.decimals();
                const amount = ethers.utils.parseUnits(paymentOptions.cryptoAmount.toString(), decimals);

                if (amount.gt(senderBalance)) {
                  // Handle insufficient balance, e.g., show an error message to the user.
                  showNotification('Insufficient balance for the transfer.');
                  setIsButtonLoading(false);
                  return;
                }

                if (signer) {
                  const tokenSigner = erc20.connect(signer);
                  const gasPrice = await ethersProvider?.getGasPrice();
                  const gasLimit = await tokenSigner.estimateGas.transfer(paymentOptions.depositAddress, amount);
                  const normalizedGasLimit = normalizeGasLimit(gasLimit.toNumber());

                  const tx = await tokenSigner.transfer(paymentOptions.depositAddress, amount, {
                    gasPrice,
                    gasLimit: normalizedGasLimit,
                  });

                  if (!tx?.hash || !paymentOptions.transactionId) {
                    showNotification();
                    setIsButtonLoading(false);
                    return null;
                  }

                  console.log('TX was send');

                  // Change the flow status to pending deposit after the transaction is sent from the wallet
                  sellState.setData({
                    txData: {
                      ...sellState?.txData,
                      frontendStatus: CryptoFrontendStatus.PENDING_DEPOSIT,
                    },
                  });

                  if (isMerchant) {
                    updateMerchantTxs(
                      {
                        transactionId: paymentOptions.transactionId,
                        sourceAddress: wallet?.accounts[0].address,
                        blockchainHash: tx.hash,
                      },
                      {
                        onSuccess: () => {
                          showNotification(NOTIFICATION_MESSAGE_PROCESSING, 'success', 'top-center');
                          setIsButtonLoading(false);
                        },
                      },
                    );
                  } else {
                    updateSellTxs(
                      {
                        transactionId: paymentOptions.transactionId,
                        sourceAddress: wallet?.accounts[0].address,
                        blockchainHash: tx.hash,
                      },
                      {
                        onSuccess: () => {
                          showNotification(NOTIFICATION_MESSAGE_PROCESSING, 'success', 'top-center');
                          setIsButtonLoading(false);
                        },
                      },
                    );
                  }
                }
              }
            } catch (e: any) {
              if (e.message.includes('INSUFFICIENT_FUNDS')) {
                showNotification('Insufficient funds');
                setIsButtonLoading(false);
              } else {
                showNotification();
                // eslint-disable-next-line no-console
                console.error(e?.message ?? '');
                setIsButtonLoading(false);
              }
            }
          }}
        >
          Pay now
        </Button>
      );
    }

    return null;
  },
);
ConnectWalletBtn.displayName = 'ConnectWalletBtn';

export const ConnectWallet = React.forwardRef<IConnectWalletRef, IConnectWallet>(
  (
    {
      onConnect,
      paymentOptions,
      isMerchant = false,
      isPlugInPayment = false,
      selectedNetworkContractAddressData,
      isBuyFlow = false,
    },
    ref,
  ) => {
    const { data } = useGetCoinNetworks([
      { cryptoCurrency: paymentOptions?.cryptoCurrency || '' },
      { enabled: !!paymentOptions?.cryptoCurrency && !isPlugInPayment },
    ]);

    const selectedNetworkContractAddress = useMemo(
      () => data?.filter((item) => item?.cryptoNetwork === paymentOptions?.selectedNetwork)?.[0]?.contractAddress,
      [data, paymentOptions?.selectedNetwork],
    );

    const sellState = useSellCrypto((state) => state, shallow);
    const buyState = useBuyCrypto((state) => state, shallow);

    return (
      <ConnectWalletBtn
        isMerchant={isMerchant}
        onConnect={onConnect}
        paymentOptions={paymentOptions}
        ref={ref}
        selectedNetwork={
          (isBuyFlow ? buyState.network : sellState?.network || sellState?.txData?.cryptoNetwork) as CryptoCurrency
        }
        selectedNetworkContractAddress={
          isPlugInPayment ? selectedNetworkContractAddressData : selectedNetworkContractAddress
        }
      />
    );
  },
);
ConnectWallet.displayName = 'ConnectWallet';
